import * as React from "react";
import { SimpleContainerProps, SimpleContainerFTNodeOptions } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { getExtraClassNameBasedOnParameters } from "./utilityStyles";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { normalizeOptionsData, hasRenderableChilren } from "stores/FlexTemplateStore";

export interface SimpleContainerContext {
  modulesHaveBorder?: boolean;
}

const defaultContext = {
  modulesHaveBorder: false,
};

export const SimpleContainerContext = React.createContext<SimpleContainerContext>(defaultContext);

const DEFAULT_WIDTH = "100";

export const SimpleContainer = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: SimpleContainerProps) => {
    const { context, blossomComponent, flexModuleModelStore, flexTemplateNode } = props;

    /* istanbul ignore next */
    if (!blossomComponent) {
      return null;
    }

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as SimpleContainerFTNodeOptions;

    const {
      containerName,
      mobileWidth,
      title,
      width,
      innerTitle,
      tabletWidth,
      desktopWidth,
      hasBorder: modulesHaveBorder,
    } = options;

    if (!hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    const headingClass = "uitk-type-display-700 uitk-cell all-cell-1-1";
    const regionSpecificClasses = getRegionSpecificClasses(containerName);
    const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
    const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
    const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];
    const flexContent = (
      <>
        {title && <h2 className={headingClass}>{title}</h2>}
        {innerTitle && <h2 className={headingClass}>{innerTitle}</h2>}
        <BlossomComponents blossomComponent={blossomComponent} context={context} />
      </>
    );

    const commonClassNames = ["uitk-grid uitk-cell", regionSpecificClasses, getExtraClassNameBasedOnParameters(options)]
      .filter((classes) => classes !== "")
      .join(" ");

    return (
      <SimpleContainerContext.Provider value={{ modulesHaveBorder }}>
        <Viewport>
          {mobileWidth === "hidden" ? (
            <ViewSmall />
          ) : (
            <ViewSmall>
              <div
                className={`SimpleContainer ${
                  containerName === "dx-page-header" ? " elevation-4" : ""
                } ${commonClassNames} ${gridMobileWidth} `}
                role="region"
              >
                {flexContent}
              </div>
            </ViewSmall>
          )}

          {tabletWidth === "hidden" ? (
            <ViewMedium />
          ) : (
            <ViewMedium>
              <div
                className={`SimpleContainer ${
                  containerName === "dx-page-header" ? " elevation-4" : ""
                } ${commonClassNames} ${gridTabletWidth} `}
                role="region"
              >
                {flexContent}
              </div>
            </ViewMedium>
          )}

          {desktopWidth === "hidden" ? (
            <ViewLarge />
          ) : (
            <ViewLarge>
              <div
                className={`${commonClassNames} ${gridWidth} ${
                  containerName === "dx-page-header" ? "elevation-4" : " SimpleContainer"
                }`}
                role="region"
              >
                {flexContent}
              </div>
            </ViewLarge>
          )}
        </Viewport>
      </SimpleContainerContext.Provider>
    );
  })
);

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */
const getRegionSpecificClasses = (regionName?: string): string => {
  switch (regionName) {
    case "wizard":
      return "wizardHero all-b-margin-three whiteBackground";
    case "FullWidthWizard":
      return "wizardHero fullWidth";
    case "FullWidthBreadcrumbs":
      return "fullWidthBreadcrumbs";
    case "CallToActionGroup":
      return "callToActionGroup";
    case "all-b-padding-three":
      return "all-b-padding-three";
    case "StorefrontConfidence":
      return "storefrontConfidence";
    case "all-cell-1-3":
      return "subRegion all-cell-1-3";
    case "dx-sidebar-content":
      return "dx-sidebar-content";
    case "SEO links":
      return "SEOLinks";
    case "CenteredDestinationWizard":
      return "CenteredDestinationWizard";
    case "FullWidthConfidenceMessages":
      return "confidenceMessages fullWidth";
    case "RecentlyBookedReviewedNoBorderBkg":
      return "recentlyBookedReviewed noBorder noBackground";
    case "NativeShowcase":
      return "all-y-padding-three";
    case "Weekend Getaways":
      return "all-t-padding-three";
    case "PropertyHeroImage":
      return "m-l-padding-three l-l-padding-three";
    case "PropertyReviewHeadline":
      return "m-r-padding-three l-r-padding-three";
  }

  return "";
};

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1",
};

export default SimpleContainer;
